import { useState, useEffect } from "react";
import { Funnel, Envelope, Phone, ChatText, CalendarBlank, FileCsv, FilePdf, PaperPlaneTilt, Trash } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function initials(name) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

const TINTS = [
  { bg: "rgba(26,92,55,0.1)", color: "#1A5C37" },
  { bg: "rgba(184,92,56,0.1)", color: "#B85C38" },
  { bg: "rgba(74,84,80,0.12)", color: "#4A5450" },
  { bg: "rgba(26,92,55,0.15)", color: "#14482B" },
  { bg: "rgba(184,92,56,0.15)", color: "#964B2E" },
];

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function csvCell(value) {
  const text = value == null ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function pdfText(value) {
  return String(value ?? "")
    .replace(/[^\x20-\x7E]/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function wrapText(text, maxChars = 84) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines.length ? lines : [""];
}

function createLeadsPdf(leads) {
  const lines = [
    { text: "KYBIA Leads", size: 18 },
    { text: `Exported: ${new Date().toLocaleString("en-IN")}`, size: 10 },
    { text: `Total leads: ${leads.length}`, size: 10 },
    { text: "", size: 10 },
  ];

  leads.forEach((lead, idx) => {
    lines.push({ text: `${idx + 1}. ${lead.name || ""}`, size: 11 });
    lines.push({ text: `Email: ${lead.email || ""}`, size: 10 });
    lines.push({ text: `Phone: ${lead.phone || ""}`, size: 10 });
    if (lead.message) lines.push({ text: `Message: ${lead.message}`, size: 10 });
    lines.push({ text: `Received: ${formatDate(lead.created_at)}`, size: 10 });
    lines.push({ text: "", size: 10 });
  });

  const pages = [];
  let pageLines = [];
  let y = 760;
  lines.forEach((entry) => {
    wrapText(entry.text).forEach((line) => {
      if (y < 64) {
        pages.push(pageLines);
        pageLines = [];
        y = 760;
      }
      pageLines.push({ text: line, size: entry.size, y });
      y -= entry.size + 6;
    });
  });
  if (pageLines.length) pages.push(pageLines);

  const objects = [];
  const addObject = (body) => {
    objects.push(body);
    return objects.length;
  };

  const fontId = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  const pageIds = [];
  const contentIds = [];
  pages.forEach((page) => {
    const stream = page.map((line) => `BT /F1 ${line.size} Tf 48 ${line.y} Td (${pdfText(line.text)}) Tj ET`).join("\n");
    contentIds.push(addObject(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`));
  });
  const pagesId = objects.length + contentIds.length + 1;
  contentIds.forEach((contentId) => {
    pageIds.push(addObject(`<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 ${fontId} 0 R >> >> /Contents ${contentId} 0 R >>`));
  });
  const pagesObjectId = addObject(`<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`);
  const catalogId = addObject(`<< /Type /Catalog /Pages ${pagesObjectId} 0 R >>`);

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((body, idx) => {
    offsets.push(pdf.length);
    pdf += `${idx + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return new Blob([pdf], { type: "application/pdf" });
}

function MessageTooltip({ message }) {
  const [visible, setVisible] = useState(false);

  return (
    <div
      className="relative mt-auto"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {/* Trigger row — always visible, message truncated to 1 line */}
      <div
        className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg cursor-default"
        style={{ backgroundColor: "rgba(26,92,55,0.04)", borderLeft: "2px solid rgba(26,92,55,0.25)" }}
      >
        <ChatText size={12} weight="duotone" className="flex-shrink-0" style={{ color: "#1A5C37" }} />
        <p className="text-xs truncate" style={{ color: "#4A5450" }}>{message}</p>
      </div>

      {/* Tooltip — full message */}
      {visible && (
        <div
          className="absolute z-50 bottom-full left-0 mb-2 p-4 rounded-xl shadow-xl text-xs leading-relaxed pointer-events-none whitespace-pre-wrap break-words"
          style={{
            backgroundColor: "#1C211F",
            color: "#F7F5F0",
            width: "360px",
            maxWidth: "calc(100vw - 48px)",
            maxHeight: "280px",
            overflowY: "auto",
            boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          }}
        >
          {message}
          {/* Arrow */}
          <span
            className="absolute top-full left-4"
            style={{
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid #1C211F",
              display: "block",
              width: 0,
              height: 0,
            }}
          />
        </div>
      )}
    </div>
  );
}

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [actionMessage, setActionMessage] = useState("");

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/leads`)
      .then((r) => r.json())
      .then((data) => setLeads(Array.isArray(data) ? data : []))
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  }, []);

  const exportCsv = () => {
    const headers = ["Name", "Email", "Phone", "Message", "Received At"];
    const rows = leads.map((lead) => [
      lead.name,
      lead.email,
      lead.phone,
      lead.message,
      lead.created_at ? new Date(lead.created_at).toLocaleString("en-IN") : "",
    ]);
    const csv = `\uFEFF${[headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\n")}`;
    downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), "leads.csv");
  };

  const exportPdf = () => {
    downloadBlob(createLeadsPdf(leads), "leads.pdf");
  };

  const handleSendMail = async (leadId) => {
    setSendingId(leadId);
    setActionMessage("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}/send-mail`, { method: "POST" });
      if (!res.ok) throw new Error("Send failed");
      setActionMessage("Mail sent successfully.");
    } catch {
      setActionMessage("Mail could not be sent. Check mail settings.");
    } finally {
      setSendingId(null);
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm("Delete this lead?")) return;
    setDeletingId(leadId);
    setActionMessage("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      setLeads((current) => current.filter((lead) => lead.id !== leadId));
      setActionMessage("Lead deleted.");
    } catch {
      setActionMessage("Lead could not be deleted.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h3 className="font-heading text-xl font-medium" style={{ color: "#1C211F" }}>Leads</h3>
          <p className="text-sm mt-0.5" style={{ color: "#4A5450" }}>
            {leads.length} lead{leads.length !== 1 ? "s" : ""} received
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportCsv}
            disabled={loading || leads.length === 0}
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors"
            style={{ color: "#1A5C37", borderColor: "rgba(26,92,55,0.25)", opacity: loading || leads.length === 0 ? 0.45 : 1 }}
          >
            <FileCsv size={16} weight="duotone" />
            Export CSV
          </button>
          <button
            type="button"
            onClick={exportPdf}
            disabled={loading || leads.length === 0}
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: "#1A5C37", color: "#F7F5F0", opacity: loading || leads.length === 0 ? 0.45 : 1 }}
          >
            <FilePdf size={16} weight="duotone" />
            Export PDF
          </button>
        </div>
      </div>

      {actionMessage && (
        <p className="text-xs px-3 py-2 rounded-lg mb-4" style={{ color: "#1A5C37", backgroundColor: "rgba(26,92,55,0.08)" }}>
          {actionMessage}
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }} />
        </div>
      ) : leads.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(26,92,55,0.08)" }}>
            <Funnel size={28} weight="duotone" style={{ color: "#1A5C37" }} />
          </div>
          <p className="text-sm font-medium" style={{ color: "#1C211F" }}>No leads yet</p>
          <p className="text-xs mt-1" style={{ color: "#4A5450" }}>
            Submissions from the &quot;Get Involved&quot; form will appear here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
          {leads.map((lead, idx) => {
            const tint = TINTS[idx % TINTS.length];
            return (
              <div
                key={lead.id}
                className="flex flex-col p-4 rounded-xl border"
                style={{ backgroundColor: "#fff", borderColor: "rgba(26,92,55,0.1)" }}
                data-testid={`lead-card-${lead.id}`}
              >
                {/* Avatar + name + date */}
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
                    style={{ backgroundColor: tint.bg, color: tint.color }}
                  >
                    {initials(lead.name)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: "#1C211F" }}>{lead.name}</p>
                    <p className="flex items-center gap-1 text-xs" style={{ color: "#7A8A84" }}>
                      <CalendarBlank size={10} weight="duotone" />
                      {formatDate(lead.created_at)}
                    </p>
                  </div>
                </div>

                {/* Email */}
                <a
                  href={`mailto:${lead.email}`}
                  className="flex items-center gap-1.5 text-xs mb-1.5 truncate"
                  style={{ color: "#4A5450" }}
                >
                  <Envelope size={12} weight="duotone" style={{ color: "#1A5C37", flexShrink: 0 }} />
                  <span className="truncate">{lead.email}</span>
                </a>

                {/* Phone */}
                <a
                  href={`tel:${lead.phone}`}
                  className="flex items-center gap-1.5 text-xs mb-3"
                  style={{ color: "#4A5450" }}
                >
                  <Phone size={12} weight="duotone" style={{ color: "#1A5C37", flexShrink: 0 }} />
                  {lead.phone}
                </a>

                {/* Message — tooltip on hover */}
                {lead.message && <MessageTooltip message={lead.message} />}

                <div className="flex items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: "rgba(26,92,55,0.08)" }}>
                  <button
                    type="button"
                    onClick={() => handleSendMail(lead.id)}
                    disabled={sendingId === lead.id || deletingId === lead.id}
                    className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-medium border transition-colors"
                    style={{ color: "#1A5C37", borderColor: "rgba(26,92,55,0.25)", opacity: sendingId === lead.id ? 0.6 : 1 }}
                  >
                    <PaperPlaneTilt size={13} weight="duotone" />
                    {sendingId === lead.id ? "Sending..." : "Send mail"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(lead.id)}
                    disabled={deletingId === lead.id || sendingId === lead.id}
                    className="flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-medium border transition-colors"
                    style={{ color: "#B85C38", borderColor: "rgba(184,92,56,0.25)", opacity: deletingId === lead.id ? 0.6 : 1 }}
                  >
                    <Trash size={13} weight="duotone" />
                    {deletingId === lead.id ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
